import { Controller } from "@hotwired/stimulus";
import {
  GridApi,
  ColDef,
  IRowNode,
  ModuleRegistry,
  AllEnterpriseModule,
  LicenseManager,
  RowSelectedEvent,
} from "ag-grid-enterprise";
import type {
  SideBarDef,
  ToolPanelDef,
  MenuItemDef,
  DefaultMenuItem,
  GetMainMenuItemsParams,
  GridState,
  GridStateKey,
  SideBarState,
} from "ag-grid-enterprise";
import { showConfirmModal } from "../ag_grid_shared/confirm_modal";
import { mapColDefTree } from "../ag_grid_shared/col_def_tree";

ModuleRegistry.registerModules([AllEnterpriseModule]);

export const MIN_GRID_HEIGHT = 400;
// Small breathing room at the viewport bottom — footer is pushed below the fold
export const GRID_BOTTOM_MARGIN = 24;
// Layout changes arrive in bursts (a drag emits many columnSizing events); coalesce them.
export const GRID_STATE_SAVE_DEBOUNCE_MS = 2000;
// Below this a cell shows two or three characters, so scrolling beats squeezing.
export const MIN_USABLE_FLEX_WIDTH = 120;

// AG-Grid's id for the agNewFiltersToolPanel we register in buildSideBarConfig. Both the
// header's "Clear all" affordance and the card-list reset key off it.
export const FILTERS_PANEL_ID = "filters-new";

// Titles for the tool panel header bar we mount (see mountToolPanelHeader). Keyed by the
// tool panel ids declared in buildSideBarConfig.
export const TOOL_PANEL_TITLES: Record<string, string> = {
  columns: "Columns",
  [FILTERS_PANEL_ID]: "Filters",
};

// Every GridStateKey except `sideBar`, for api.setState's propertiesToIgnore — see
// resetFilterPanelCards, which must apply the side bar and touch nothing else.
// Declared as a Record so a future AG-Grid upgrade that adds a state key fails to compile
// here rather than silently letting setState reapply that key.
const NON_SIDE_BAR_STATE_KEYS: Record<Exclude<GridStateKey, "sideBar">, true> = {
  aggregation: true,
  cellSelection: true,
  columnGroup: true,
  columnOrder: true,
  columnPinning: true,
  columnSizing: true,
  columnVisibility: true,
  filter: true,
  focusedCell: true,
  pagination: true,
  pivot: true,
  rowGroup: true,
  rowGroupExpansion: true,
  rowPinning: true,
  rowSelection: true,
  scroll: true,
  sort: true,
  ssrmRowGroupExpansion: true,
};

// Saved grid state is AG Grid's GridState plus `mode`, which is ours: the basic/advanced view is not
// part of AG Grid's state but has to survive navigation with the layout it belongs to.
export type PersistedGridState = Record<string, unknown> & { mode?: "basic" | "advanced" };

export const EMPTY_SET: ReadonlySet<string> = Object.freeze(new Set<string>());

type RowAction = Record<string, string> & {
  appendSelectedIds?: boolean;
  appendSelectedIdsParam?: string;
  selectedIdsInBody?: boolean;
  selectionTokenUrl?: string;
  selectionTokenParam?: string;
  disabled?: boolean;
  emitEvent?: string;
  remote?: boolean;
};
type StaticAction = AgGridBaseController["selectionActionsValue"][number];

// Row data is Record<string, unknown>, so an id arrives untyped. The row-actions endpoint keys on a
// scalar, so anything else is no id at all rather than something String() can mangle into a URL.
const rowActionId = (row: Record<string, unknown> | undefined): string | number | null => {
  const id = row?.id;
  return typeof id === "string" || typeof id === "number" ? id : null;
};

export class AgGridBaseController extends Controller {
  static targets = [
    "grid",
    "actionsMenu",
    "selectedCount",
    "viewToggleBasic",
    "viewToggleAdvanced",
    "viewToggleMobile",
    "columnsToggle",
    "filterToggle",
    "exportToggle",
    "exportDropdown",
    "clearFiltersButton",
  ];

  static values = {
    licenseKey:        String,
    columnDefs:        Array,
    gridOptions:       Object,
    gridStateUrl:      String,
    advancedColumnDefs: Array,
    selectionActions:  Array,
    followSelection:   Boolean,
    followSelectionColumn: String,
    rowActionsField:   String,
    rowActionsUrl:     String,
    hideAggregationPanels: Boolean,
    // Every grid's actions menu is draggable unless a call site opts out.
    draggableMenu:     { type: Boolean, default: true },
  };

  declare readonly gridTarget: HTMLElement;
  declare readonly hasGridTarget: boolean;
  declare readonly actionsMenuTarget: HTMLElement;
  declare readonly hasActionsMenuTarget: boolean;
  declare readonly selectedCountTarget: HTMLElement;
  declare readonly hasSelectedCountTarget: boolean;
  declare readonly viewToggleBasicTarget: HTMLButtonElement;
  declare readonly viewToggleAdvancedTarget: HTMLButtonElement;
  declare readonly hasViewToggleBasicTarget: boolean;
  declare readonly hasViewToggleAdvancedTarget: boolean;
  declare readonly viewToggleMobileTarget: HTMLButtonElement;
  declare readonly hasViewToggleMobileTarget: boolean;
  declare readonly columnsToggleTarget: HTMLButtonElement;
  declare readonly hasColumnsToggleTarget: boolean;
  declare readonly filterToggleTarget: HTMLButtonElement;
  declare readonly hasFilterToggleTarget: boolean;
  declare readonly exportToggleTarget: HTMLButtonElement;
  declare readonly exportDropdownTarget: HTMLElement;
  declare readonly hasExportToggleTarget: boolean;
  declare readonly hasExportDropdownTarget: boolean;
  declare readonly clearFiltersButtonTarget: HTMLButtonElement;
  declare readonly hasClearFiltersButtonTarget: boolean;

  declare readonly licenseKeyValue: string;
  declare readonly columnDefsValue: ColDef[];
  declare readonly gridOptionsValue: Record<string, unknown>;
  declare readonly gridStateUrlValue: string;
  declare readonly advancedColumnDefsValue: ColDef[];
  declare readonly hideAggregationPanelsValue: boolean;
  declare readonly selectionActionsValue: Array<{
    label: string;
    url?: string;
    urlTemplate?: string;
    urlFromRowField?: string;
    icon?: string;
    method?: string;
    rel?: string;
    confirm?: string;
    turbo_frame?: string;
    target?: string;
    // Optional heading and red lead-in line for the confirm dialog — the same trio a per-row
    // action carries, so a static action and a row action describe their dialog the same way.
    confirm_title?: string;
    confirm_danger?: string;
    appendSelectedIds?: boolean;
    appendSelectedIdsParam?: string;
    // Ids go in a POST body under appendSelectedIdsParam instead of the URL: ~1,000 `filter[]=`
    // ids overflow the server's query-string cap. Only honoured by refreshAfterDownload actions.
    selectedIdsInBody?: boolean;
    // Ids are POSTed here first (as appendSelectedIdsParam) and the link opens with the returned
    // `selection_token` instead of the id list — for actions that navigate or open a modal/iframe.
    selectionTokenUrl?: string;
    selectionTokenParam?: string;
    minSelection?: number;
    maxSelection?: number;
    batchDelete?: boolean;
    requireAllRowsField?: string;
    // Softer sibling of requireAllRowsField: instead of hiding the action as soon as one
    // selected row lacks the field, narrow the appended ids to the rows that DO carry it and
    // hide only when none qualify. For a grid whose rows are not all the same entity (the root
    // dashboard mixes elections and vote box rows), requireAllRowsField would take a
    // select-all — which almost always sweeps in a box row — and leave the customer with no
    // bulk actions at all.
    restrictSelectedIdsToRowField?: string;
    refreshAfterDownload?: boolean;
    hideOnSelectAll?: boolean;
    emitEvent?: string;
    remote?: boolean;
  }>;
  declare readonly followSelectionValue: boolean;
  declare readonly followSelectionColumnValue: string;
  declare readonly hasFollowSelectionColumnValue: boolean;
  declare readonly rowActionsFieldValue: string;
  declare readonly rowActionsUrlValue: string;
  declare readonly draggableMenuValue: boolean;
  declare readonly hasRowActionsUrlValue: boolean;

  protected gridApi: GridApi | null = null;

  protected readonly rowSelectableCallback = (node: IRowNode): boolean =>
    (node.data as Record<string, unknown>)?.row_selectable !== false;
  protected isBasicMode = true;
  protected isRestoringState = false;
  private saveGridStateTimer: ReturnType<typeof setTimeout> | null = null;
  private lastPersistedState: string | null = null;
  protected pendingAutoSize = false;
  protected hasRowSelection = false;
  protected resizeHandler: (() => void) | null = null;
  private heightObserver: ResizeObserver | null = null;
  protected outsideClickHandler: ((e: MouseEvent) => void) | null = null;
  protected gridBlurHandler: ((e: MouseEvent) => void) | null = null;
  private actionAjaxSuccessHandler: ((e: Event) => void) | null = null;
  private modalRefreshObserver: MutationObserver | null = null;
  private lastClickX: number | null = null;
  // getSelectedNodes() is ordered by row index, not by selection chronology — checking
  // an earlier row after a later one otherwise anchors the menu to the wrong row.
  private followAnchorNodeId: string | null = null;
  private pendingFollowScrollFrame = false;
  // Set once the user drags the actions menu — pins it until the selection clears.
  private menuDragged = false;
  private lazyRowActionsToken = 0;
  private rowActionsCache = new Map<string, RowAction[]>();
  private rowActionsInflight = new Map<string, Promise<RowAction[]>>();
  private pendingAutoSizeNonFlexFrame = false;
  // Columns the queued autosize pass will measure; null means every displayed column.
  private pendingAutoSizeScope: string[] | null = null;

  connect() {
    if (this.licenseKeyValue) {
      LicenseManager.setLicenseKey(this.licenseKeyValue);
    }
    this.outsideClickHandler = (e: MouseEvent) => {
      const exportOpen = this.hasExportDropdownTarget && !this.exportDropdownTarget.hidden;
      if (exportOpen) {
        if (
          !this.exportToggleTarget.contains(e.target as Node) &&
          !this.exportDropdownTarget.contains(e.target as Node)
        ) {
          this.closeExportDropdown();
        }
      }
    };
    document.addEventListener("click", this.outsideClickHandler);

    // Delegate from the persistent menu target so one listener survives every
    // renderActions() rebuild of the actions list underneath it.
    if (this.hasActionsMenuTarget) {
      this.actionAjaxSuccessHandler = (e: Event) => {
        const target = e.target as HTMLElement;
        if (!target.matches(".ag-grid-component-action-link[data-remote]")) return;
        if (target.matches('[rel~="modal:open"]')) return;
        this.refreshAfterRemoteAction();
      };
      this.actionsMenuTarget.addEventListener("ajax:success", this.actionAjaxSuccessHandler);
    }

    this.setupMenuDrag();

    // jquery.modal mounts its content outside this grid's DOM, and its .js.erb response
    // detaches that content mid-dispatch — no listener outside it ever sees ajax:success.
    // Bind directly to data-ag-grid-refresh links as they're inserted instead.
    this.modalRefreshObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (!(mutation.target as HTMLElement).closest?.(".jquery-modal")) continue;
        mutation.addedNodes.forEach((node) => {
          if (!(node instanceof HTMLElement)) return;
          const targets = node.matches("[data-ag-grid-refresh]")
            ? [node]
            : Array.from(node.querySelectorAll("[data-ag-grid-refresh]"));
          targets.forEach((el) => {
            el.addEventListener("ajax:success", () => this.refreshAfterRemoteAction(), { once: true });
          });
        });
      }
    });
    this.modalRefreshObserver.observe(document.body, { childList: true, subtree: true });
  }

  // ---------------------------------------------------------------------------
  // Grid state persistence (shared by every grid that renders a gridStateUrl).
  // ---------------------------------------------------------------------------

  // Returns the user's saved state, or null when there is nothing usable. Never throws — a grid
  // must still work when the state endpoint is unreachable.
  protected async fetchGridState(): Promise<PersistedGridState | null> {
    if (!this.gridStateUrlValue) return null;
    try {
      const response = await fetch(this.gridStateUrlValue, {
        headers: { Accept: "application/json" },
        credentials: "same-origin",
      });
      if (!response.ok) return null;
      const json = (await response.json()) as { state?: PersistedGridState };
      const state = json?.state;
      return state && Object.keys(state).length > 0 ? state : null;
    } catch {
      return null;
    }
  }

  protected saveGridState(): void {
    if (!this.gridApi || !this.gridStateUrlValue || this.isRestoringState) return;
    if (this.saveGridStateTimer !== null) clearTimeout(this.saveGridStateTimer);
    this.saveGridStateTimer = setTimeout(() => {
      this.saveGridStateTimer = null;
      this.persistGridState();
    }, GRID_STATE_SAVE_DEBOUNCE_MS);
  }

  private persistGridState(): void {
    if (!this.gridApi || !this.gridStateUrlValue) return;
    const state: PersistedGridState = this.transformStateForPersist({
      ...this.gridApi.getState(),
      mode: this.isBasicMode ? "basic" : "advanced",
    });
    const body = JSON.stringify({ state });
    // Plenty of interactions re-emit state events without changing the state — a click landing on
    // the current sort, a drag that settles back. Re-PUTting an identical payload only churns
    // dead tuples on a row that already says exactly this.
    if (body === this.lastPersistedState) return;
    this.lastPersistedState = body;

    const csrfToken =
      (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content ?? "";
    fetch(this.gridStateUrlValue, {
      method: "PUT",
      headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken },
      credentials: "same-origin",
      body,
    })
      .then((response) => {
        // Clearing the marker on a rejected save lets the next identical state retry rather than
        // being deduped against a write that never landed.
        if (!response.ok) this.lastPersistedState = null;
      })
      .catch(() => {
        // Non-critical — a failed save must not break the grid
        this.lastPersistedState = null;
      });
  }

  // A renamed or dropped colId leaves the saved filter/sort naming a column that no longer
  // exists. SSRM replays both to the server, which rejects an unknown colId with a 400, and the
  // state is server-side so a reload never clears it.
  //
  // Layout keys are left alone: an orphaned entry there is cosmetic, and a rename still wants a
  // grid_identifier bump to reset saved layouts.
  protected pruneUnknownColIds(state: PersistedGridState): PersistedGridState {
    const known = this.knownColIds();
    // Columns not resolvable yet — passing the state through beats pruning every key blind.
    if (known.size === 0) return state;

    const next: PersistedGridState = { ...state };

    const filter = next.filter as Record<string, unknown> | undefined;
    if (filter) {
      const prunedFilter: Record<string, unknown> = { ...filter };
      // Every one of these is keyed by colId; advancedFilterModel is not, so it is untouched.
      ["filterModel", "columnFilterState", "selectableFilters"].forEach((key) => {
        const map = prunedFilter[key] as Record<string, unknown> | undefined;
        if (!map) return;
        prunedFilter[key] = Object.fromEntries(
          Object.entries(map).filter(([colId]) => known.has(colId))
        );
      });
      next.filter = prunedFilter;
    }

    const sort = next.sort as { sortModel?: { colId: string }[] } | undefined;
    if (sort?.sortModel) {
      next.sort = { ...sort, sortModel: sort.sortModel.filter((item) => known.has(item.colId)) };
    }

    return next;
  }

  // getColumns() is authoritative once the grid has resolved its defs; getColumnDefs() covers the
  // window before that, where a def carries no explicit colId and AG Grid derives it from `field`.
  protected knownColIds(): Set<string> {
    const columns = this.gridApi?.getColumns();
    if (columns && columns.length > 0) return new Set(columns.map((column) => column.getColId()));

    const defs = (this.gridApi?.getColumnDefs() ?? []) as ColDef[];
    return new Set(
      defs.flatMap((def) => {
        const colId = def.colId ?? def.field;
        return colId ? [colId] : [];
      })
    );
  }

  protected cancelPendingGridStateSave(): void {
    if (this.saveGridStateTimer === null) return;
    clearTimeout(this.saveGridStateTimer);
    this.saveGridStateTimer = null;
  }

  // Skips the debounce. For state changes that must survive an immediate navigation, and for
  // ones AG-Grid won't announce: api.setState emits onStateUpdated with source "api", which the
  // SSRM controller's layout-source filter ignores, so nothing else would ever save them.
  protected persistGridStateNow(): void {
    this.cancelPendingGridStateSave();
    this.persistGridState();
  }

  disconnect() {
    this.cancelPendingGridStateSave();
    this.lastPersistedState = null;
    this.isRestoringState = false;
    if (this.resizeHandler) {
      window.removeEventListener("resize", this.resizeHandler);
      this.resizeHandler = null;
    }
    // document.body outlives this controller, so skipping this leaks across Turbo navigations.
    this.heightObserver?.disconnect();
    this.heightObserver = null;
    if (this.outsideClickHandler) {
      document.removeEventListener("click", this.outsideClickHandler);
      this.outsideClickHandler = null;
    }
    if (this.gridBlurHandler) {
      document.removeEventListener("mousedown", this.gridBlurHandler);
      this.gridBlurHandler = null;
    }
    if (this.actionAjaxSuccessHandler && this.hasActionsMenuTarget) {
      this.actionsMenuTarget.removeEventListener("ajax:success", this.actionAjaxSuccessHandler);
      this.actionAjaxSuccessHandler = null;
    }
    if (this.modalRefreshObserver) {
      this.modalRefreshObserver.disconnect();
      this.modalRefreshObserver = null;
    }
    if (this.gridApi) {
      this.gridApi.destroy();
      this.gridApi = null;
    }
  }

  // Called after a non-modal remote action (e.g. Unsubscribe) completes. No-op by
  // default — SSRM overrides this to refresh via refreshServerSide().
  protected refreshAfterRemoteAction(): void {
    // Intentionally empty in the base controller.
  }

  // Download Keys is a plain file download, not an XHR — no ajax:success ever fires.
  // Fetch it manually so we get a real completion signal to refresh the grid on.
  // With `body`, the selection is POSTed as JSON instead of riding in the URL (selectedIdsInBody).
  private async downloadAndRefresh(url: string, body?: Record<string, unknown>): Promise<void> {
    let response: Response;
    try {
      response = await fetch(url, body ? this.jsonPostInit(body) : { credentials: "same-origin" });
    } catch {
      alert("Download failed: the request could not be sent. Please check your connection and try again.");
      return;
    }
    if (!response.ok) {
      alert(`Download failed (the server answered ${response.status}). Please try again.`);
      return;
    }
    const blob = await response.blob();
    const disposition = response.headers.get("content-disposition") || "";
    const match = disposition.match(/filename="?([^";]+)"?/);
    const filename = match ? match[1] : "download";
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = objectUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(objectUrl);
    this.refreshAfterRemoteAction();
  }

  // Parks the selection server-side; resolves to the token the page resolves the ids from, or null.
  private async storeSelection(url: string, body: Record<string, unknown>): Promise<string | null> {
    let response: Response;
    try {
      response = await fetch(url, this.jsonPostInit(body));
    } catch {
      alert("The selection could not be sent. Please check your connection and try again.");
      return null;
    }
    if (!response.ok) {
      alert(`The selection could not be saved (the server answered ${response.status}). Please try again.`);
      return null;
    }
    const data = (await response.json().catch(() => ({}))) as { token?: string };
    return data.token ?? null;
  }

  private jsonPostInit(body: Record<string, unknown>): RequestInit {
    const token = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? "";
    return {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": token,
        "X-Requested-With": "XMLHttpRequest",
      },
      body: JSON.stringify(body),
    };
  }

  // ── Toolbar actions ────────────────────────────────────────────────────────

  toggleColumnsPanel() {
    if (!this.gridApi) return;
    this.closeExportDropdown();
    const isOpen = this.gridApi.getOpenedToolPanel() === "columns";
    if (isOpen) {
      this.closeToolPanels();
    } else {
      // openToolPanel() only shows the inner panel, not the side bar container itself.
      this.gridApi.setSideBarVisible(true);
      this.gridApi.openToolPanel("columns");
      this.mountToolPanelHeader();
      if (this.hasColumnsToggleTarget) this.columnsToggleTarget.setAttribute("aria-pressed", "true");
      if (this.hasFilterToggleTarget) this.filterToggleTarget.setAttribute("aria-pressed", "false");
    }
  }

  toggleFilterPanel() {
    if (!this.gridApi) return;
    this.closeExportDropdown();
    const isOpen = this.gridApi.getOpenedToolPanel() === "filters-new";
    if (isOpen) {
      this.closeToolPanels();
    } else {
      // See toggleColumnsPanel() above.
      this.gridApi.setSideBarVisible(true);
      this.gridApi.openToolPanel("filters-new");
      this.mountToolPanelHeader();
      if (this.hasFilterToggleTarget) this.filterToggleTarget.setAttribute("aria-pressed", "true");
      if (this.hasColumnsToggleTarget) this.columnsToggleTarget.setAttribute("aria-pressed", "false");
    }
  }

  toggleViewMode() {
    if (this.isBasicMode) {
      this.switchToAdvanced();
    } else {
      this.switchToBasic();
    }
  }

  toggleExportDropdown() {
    if (!this.hasExportDropdownTarget) return;
    const isOpen = !this.exportDropdownTarget.hidden;
    // Close tool panels and reset their buttons so they don't appear active
    // while the export dropdown is open.
    this.closeToolPanels();
    if (isOpen) {
      this.closeExportDropdown();
    } else {
      this.exportDropdownTarget.hidden = false;
      if (this.hasExportToggleTarget) this.exportToggleTarget.setAttribute("aria-pressed", "true");
    }
  }

  // Closes whichever tool panel is open and clears both toolbar chips — the chips are a
  // pair of mutually exclusive toggles, so neither may read as pressed once nothing is open.
  protected closeToolPanels() {
    this.gridApi?.closeToolPanel();
    if (this.hasColumnsToggleTarget) this.columnsToggleTarget.setAttribute("aria-pressed", "false");
    if (this.hasFilterToggleTarget) this.filterToggleTarget.setAttribute("aria-pressed", "false");
  }

  // ── Tool panel header (close affordance) ───────────────────────────────────

  // AG-Grid's own way out of an open tool panel is its side-bar tab strip, which the theme
  // hides (`.ag-side-buttons`, eb_theme.ts) because the toolbar chips replace it — leaving an
  // opened panel with no close control of its own. Mount a title bar carrying one.
  // Every path that opens a panel (toolbar chip, restored grid state, view-mode switch)
  // emits toolPanelVisibleChanged, so this is the single place that has to hook in.
  protected setupToolPanelHeaders(): void {
    if (!this.gridApi) return;
    this.gridApi.addEventListener("toolPanelVisibleChanged", () => this.mountToolPanelHeader());
    // Filters can come and go while the panel stays open (card ✕, per-filter Clear Filter,
    // a deep-linked preset), and the header outlives all of those — so it syncs on its own.
    this.gridApi.addEventListener("filterChanged", () => this.syncClearAllFiltersButton());
    // Saved state can restore an already-open panel before any event we could hear.
    this.mountToolPanelHeader();
  }

  // Idempotent: AG-Grid unhides the wrapper before dispatching, so every caller can mount
  // straight away, and the second caller for the same panel is a no-op.
  protected mountToolPanelHeader(): void {
    if (!this.gridApi) return;
    const openPanelId = this.gridApi.getOpenedToolPanel();
    if (!openPanelId) return;
    // Each tool panel keeps its own wrapper for the grid's lifetime; only the open one is
    // unhidden, and that is how AG-Grid itself locates it.
    const wrapper = this.element.querySelector<HTMLElement>(".ag-tool-panel-wrapper:not(.ag-hidden)");
    if (!wrapper) return;
    // Filters can change while the panel is hidden (the Refine menu closes it on open), and
    // syncClearAllFiltersButton only sees a visible panel — so re-sync on every reopen.
    if (wrapper.querySelector(":scope > .ag-grid-tool-panel-header")) {
      this.syncClearAllFiltersButton();
      return;
    }

    const header = document.createElement("div");
    header.className = "ag-grid-tool-panel-header";

    const title = document.createElement("span");
    title.className = "ag-grid-tool-panel-header__title";
    title.textContent = TOOL_PANEL_TITLES[openPanelId] ?? "";

    const close = document.createElement("button");
    close.type = "button";
    close.className = "ag-grid-tool-panel-header__close";
    close.title = "Close panel";
    close.setAttribute("aria-label", "Close panel");
    close.innerHTML =
      '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" ' +
      'fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" ' +
      'stroke-linejoin="round" aria-hidden="true">' +
      '<line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>';
    close.addEventListener("click", () => this.closeToolPanels());

    // Removing filters one card at a time costs one server round trip each on SSRM grids;
    // this is the batched way out. Filters only — the toolbar's "Clear filters" chip is the
    // separate reset-everything path (sort, grouping, pivot, search).
    if (openPanelId === FILTERS_PANEL_ID) {
      const clearAll = document.createElement("button");
      clearAll.type = "button";
      clearAll.className = "ag-grid-tool-panel-header__clear-all";
      clearAll.textContent = "Clear all";
      clearAll.title = "Remove all filters";
      clearAll.addEventListener("click", () => this.clearAllPanelFilters());
      header.append(title, clearAll, close);
    } else {
      header.append(title, close);
    }

    wrapper.prepend(header);
    this.syncClearAllFiltersButton();
  }

  // Nothing to clear reads as a dead control, so the button says so rather than lying.
  private syncClearAllFiltersButton(): void {
    const button = this.element.querySelector<HTMLButtonElement>(
      ".ag-tool-panel-wrapper:not(.ag-hidden) > .ag-grid-tool-panel-header .ag-grid-tool-panel-header__clear-all"
    );
    if (!button || !this.gridApi) return;
    button.disabled = !this.hasActiveFilters();
  }

  // The single definition of "a filter is active". The panel header's Clear all clears filters and
  // nothing else, so it syncs on this alone.
  protected hasActiveFilters(): boolean {
    return Object.keys(this.gridApi?.getFilterModel() ?? {}).length > 0;
  }

  // Everything a Clear-filters control undoes that every row model knows about. Subclasses that
  // reset more than this override and OR their own dimensions in.
  protected hasActiveGridState(): boolean {
    if (!this.gridApi) return false;

    return (
      this.hasActiveFilters() ||
      (this.gridApi.getRowGroupColumns() ?? []).length > 0 ||
      this.gridApi.isPivotMode()
    );
  }

  // The toolbar chip hides where the panel header's button disables: it sits in a row of chips, and
  // a permanently greyed one there reads as broken rather than inactive.
  protected syncClearFiltersButton(): void {
    if (!this.gridApi || !this.hasClearFiltersButtonTarget) return;

    this.clearFiltersButtonTarget.style.display = this.hasActiveGridState() ? "" : "none";
  }

  // Clears every filter in ONE pass and takes the now-empty cards down with it.
  //
  // Order is the whole design:
  //   1. setFilterModel({}) first — a batched onFilterChanged, so SSRM refetches once. Tearing
  //      cards down while their models are still live would instead fire one filter-change per
  //      card (FilterPanelService.remove -> destroyFilter -> onFilterChanged when wasActive).
  //   2. read getState() AFTER that — capturing it first writes the old filter model straight
  //      back in step 3 and resurrects the filters inside the same click.
  //   3. setState with the FULL state spread. StateService assigns `cachedState = state`
  //      wholesale, so a bare { sideBar } would truncate the cache and the next save would drop
  //      the user's column widths, order, visibility and sort. propertiesToIgnore keeps the
  //      write itself surgical: only the side bar is actually applied.
  //   4. remount the header — SideBarComp.setState rebuilds the panel wrappers and ours goes
  //      with them.
  protected clearAllPanelFilters(): void {
    if (!this.gridApi) return;
    this.gridApi.setFilterModel({});
    this.resetFilterPanelCards();
    this.persistGridStateNow();
  }

  // Steps 2-4 above, without touching the filter model. Callers that reset the model by another
  // route (SSRM's resetGridState) need the cards cleared too: FilterPanelService.updateFilterStates
  // creates a card for every colId in the model but never removes one whose model went null.
  protected resetFilterPanelCards(): void {
    if (!this.gridApi) return;
    const current = this.gridApi.getState();
    const sideBar = current.sideBar as SideBarState | undefined;
    if (!sideBar?.toolPanels || !(FILTERS_PANEL_ID in sideBar.toolPanels)) return;

    const next: GridState = {
      ...current,
      sideBar: {
        ...sideBar,
        toolPanels: { ...sideBar.toolPanels, [FILTERS_PANEL_ID]: { filters: [] } },
      },
    };
    this.gridApi.setState(next, Object.keys(NON_SIDE_BAR_STATE_KEYS) as GridStateKey[]);
    this.mountToolPanelHeader();
    this.syncClearAllFiltersButton();
  }

  // ── Abstract — concrete controllers must override ──────────────────────────

  switchToBasic(): void {
    throw new Error(`${this.constructor.name} must implement switchToBasic()`);
  }

  switchToAdvanced(): void {
    throw new Error(`${this.constructor.name} must implement switchToAdvanced()`);
  }

  // ── Public API for sibling controllers ─────────────────────────────────────

  // A sibling view (the dashboard calendar) can take the panel over from the grid; the chrome
  // that only acts on the grid — Columns, Filters, Export — has nothing to act on while it does.
  // Toggles `hidden` rather than `style.display` so the tier's own display bookkeeping
  // (switchToBasic/switchToAdvanced) still decides what comes back on the way out.
  setGridChromeHidden(hidden: boolean): void {
    if (this.hasColumnsToggleTarget) this.columnsToggleTarget.hidden = hidden;
    if (this.hasFilterToggleTarget) this.filterToggleTarget.hidden = hidden;
    if (this.hasExportToggleTarget) this.exportToggleTarget.hidden = hidden;
    if (!hidden) return;

    this.closeExportDropdown();
    this.closeToolPanels();
  }

  // ── Protected helpers ──────────────────────────────────────────────────────

  protected setActiveToggle(mode: "basic" | "advanced") {
    if (!this.hasViewToggleBasicTarget || !this.hasViewToggleAdvancedTarget) return;
    const ACTIVE = "ag-grid-view-toggle__btn--active";
    if (mode === "basic") {
      this.viewToggleBasicTarget.classList.add(ACTIVE);
      this.viewToggleAdvancedTarget.classList.remove(ACTIVE);
    } else {
      this.viewToggleAdvancedTarget.classList.add(ACTIVE);
      this.viewToggleBasicTarget.classList.remove(ACTIVE);
    }
    if (this.hasViewToggleMobileTarget) {
      this.viewToggleMobileTarget.textContent = mode === "basic" ? "Basic" : "Advanced";
    }
  }

  protected closeExportDropdown() {
    if (!this.hasExportDropdownTarget) return;
    this.exportDropdownTarget.hidden = true;
    if (this.hasExportToggleTarget) this.exportToggleTarget.setAttribute("aria-pressed", "false");
  }

  protected initDynamicHeight() {
    this.resizeHandler = () => this.updateGridHeight();
    window.addEventListener("resize", this.resizeHandler);
    // Content appearing above the grid after it sized itself (the JS-injected flash banner, a
    // late webfont) pushes it down without firing a resize event, leaving a too-tall grid
    // hanging past the window bottom. No feedback loop: updateGridHeight() early-returns on a
    // sub-pixel delta, so the height change this triggers settles on the next pass.
    this.heightObserver = new ResizeObserver(() => this.updateGridHeight());
    this.heightObserver.observe(document.body);
    // Iterate until height converges — necessary when the grid is inside a flex-centered
    // container (e.g. a modal) where the container's position depends on the grid's own
    // height (circular dependency). Page-mounted grids converge in 1-2 passes.
    let passes = 0;
    const measure = () => {
      const changed = this.updateGridHeight();
      if (changed && ++passes < 3) requestAnimationFrame(measure);
    };
    requestAnimationFrame(measure);
  }

  protected updateGridHeight(): boolean {
    if (!this.hasGridTarget) return false;
    const rect = this.gridTarget.getBoundingClientRect();
    const available = window.innerHeight - rect.top - GRID_BOTTOM_MARGIN;
    const newHeight = Math.max(available, MIN_GRID_HEIGHT);
    const prevHeight = parseFloat(this.gridTarget.style.height) || 0;
    if (Math.abs(newHeight - prevHeight) < 1) return false;
    this.gridTarget.style.height = `${newHeight}px`;
    return true;
  }

  protected syncPanelButtonStates() {
    if (!this.gridApi) return;
    const openPanel = this.gridApi.getOpenedToolPanel();
    if (this.hasColumnsToggleTarget) {
      this.columnsToggleTarget.setAttribute("aria-pressed", openPanel === "columns" ? "true" : "false");
    }
    if (this.hasFilterToggleTarget) {
      this.filterToggleTarget.setAttribute("aria-pressed", openPanel === "filters-new" ? "true" : "false");
    }
  }

  // Avoids AG-Grid's "count(Count)" aggFunc-prefixed header label on value columns.
  protected readonly suppressAggFuncInHeader = true;

  // Prevents a stuck wrap-text/auto-height header once Pivot Mode has been toggled at least
  // once. Spread into `defaultColDef` and `autoGroupColumnDef`.
  protected readonly pivotHeaderQuirkColDefOverrides = {
    wrapHeaderText: false,
    autoHeaderHeight: false,
  } as const;

  protected preserveFlexOnAutoSize(): boolean {
    return false;
  }

  // Covers both measurement and the trailing flex fill.
  protected autoSizeExcludedColIds(): ReadonlySet<string> {
    return EMPTY_SET;
  }

  // Last word on the PUT body; the default persists AG Grid's full state as today.
  protected transformStateForPersist(state: PersistedGridState): PersistedGridState {
    return state;
  }

  // Runs after the header-menu "Reset Columns" action has reset and re-autosized.
  protected afterResetColumns(): void {}

  // `scopeColIds` limits which columns are measured; autoSizeColumns walks every rendered cell.
  protected autoSizeNonFlexColumns(scopeColIds?: string[]) {
    if (!this.gridApi) return;
    // Called on every onCellValueChanged — a multi-cell paste fires that once per cell,
    // so without coalescing, an N-cell paste would trigger N full-grid autosize passes.
    if (this.pendingAutoSizeNonFlexFrame) {
      this.widenPendingAutoSizeScope(scopeColIds);
      return;
    }
    this.pendingAutoSizeNonFlexFrame = true;
    this.pendingAutoSizeScope = scopeColIds ?? null;
    requestAnimationFrame(() => {
      this.pendingAutoSizeNonFlexFrame = false;
      const scope = this.pendingAutoSizeScope;
      this.pendingAutoSizeScope = null;
      if (!this.gridApi) return;
      const visibleCols = this.gridApi.getAllDisplayedColumns() ?? [];
      if (visibleCols.length === 0) return;
      // A hidden grid (display:none ancestor) measures every cell empty and has no room; this pass
      // would content-size to padding and null flex. Whatever reveals the grid must re-run autosize.
      if (this.gridTarget.clientWidth === 0) return;
      // Read here, not at scheduling time — the excluded set may be seeded (a restore landing)
      // between when this pass was queued and when it actually runs.
      const excludedColIds = new Set<string>([
        "ag-Grid-RowNumbersColumn",
        "ag-Grid-SelectionColumn",
        ...this.autoSizeExcludedColIds(),
      ]);
      const preserveFlex = this.preserveFlexOnAutoSize() && this.hasRoomToFitColumns();
      const colIds = (preserveFlex
        ? visibleCols.filter((col) => col.getColDef().flex == null)
        : visibleCols
      )
        .map((col) => col.getColId())
        .filter((colId) => !excludedColIds.has(colId));
      const measuredColIds = scope ? colIds.filter((colId) => scope.includes(colId)) : colIds;
      if (measuredColIds.length > 0) {
        this.gridApi.autoSizeColumns(measuredColIds);

        // Autosize only measures the column's current (usually unsorted) header, with no
        // allowance for the sort icon that appears once a column is actively sorted -- pad
        // every column with a little slack up front so sorting doesn't wrap/overlap the label.
        const sortIconAllowance = 5;
        this.gridApi.applyColumnState({
          state: measuredColIds.map((colId) => ({
            colId,
            width: (this.gridApi?.getColumn(colId)?.getActualWidth() ?? 0) + sortIconAllowance,
            // autoSizeColumns leaves the colDef's flex alone, so the measured width needs it cleared.
            ...(preserveFlex ? {} : { flex: null }),
          })),
        });
      }

      // A column already flexing owns the remaining width; a second flex:1 would split it. State flex,
      // not colDef: a non-preserve pass nulls state flex, and then only the fill can take the slack.
      const hasFlexCol = preserveFlex && visibleCols.some((col) => col.getFlex() != null);
      const containerWidth = this.gridTarget.clientWidth;
      const totalColWidth = visibleCols.reduce((sum, col) => sum + col.getActualWidth(), 0);
      if (!hasFlexCol && totalColWidth < containerWidth && colIds.length > 0) {
        const lastColId = colIds[colIds.length - 1];
        this.gridApi.applyColumnState({
          state: [{ colId: lastColId, flex: 1 }],
        });
      }
    });
  }

  // Fitting divides the container rather than overflowing it, so too narrow squeezes to AG Grid's
  // ~36px floor instead of scrolling. Counted, not measured: the widths are this call's own output.
  protected hasRoomToFitColumns(): boolean {
    const cols = this.gridApi?.getAllDisplayedColumns() ?? [];
    const flexColCount = cols.filter((col) => col.getColDef().flex != null).length;
    if (flexColCount === 0) return true;

    const reserved = cols
      .filter((col) => col.getColDef().flex == null)
      .reduce((sum, col) => sum + col.getActualWidth(), 0);

    return this.gridTarget.clientWidth - reserved >= flexColCount * MIN_USABLE_FLEX_WIDTH;
  }

  // A queued pass absorbs later requests, so it has to end up as wide as the widest of them.
  private widenPendingAutoSizeScope(scopeColIds?: string[]): void {
    if (this.pendingAutoSizeScope === null) return;
    if (!scopeColIds) {
      this.pendingAutoSizeScope = null;
      return;
    }
    this.pendingAutoSizeScope = [...new Set([...this.pendingAutoSizeScope, ...scopeColIds])];
  }

  protected renderActions(selectedRows: Array<Record<string, unknown>>): void {
    this.lazyRowActionsToken++;

    const actionsContainer = this.actionsMenuTarget.querySelector(
      ".ag-grid-component-actions-menu__actions"
    );
    if (!actionsContainer) return;

    actionsContainer.innerHTML = "";

    const selectedIds = selectedRows
      .map((row) => row.id)
      .filter((id) => id !== undefined && id !== null) as (string | number)[];

    const count = selectedRows.length;

    const filteredStaticActions = this.selectionActionsValue.filter((action) => {
      if (action.minSelection !== undefined && count < action.minSelection) return false;
      if (action.maxSelection !== undefined && count > action.maxSelection) return false;
      // Only loaded rows reach an action's URL, so an action that must not act on a partial
      // selection opts out of select-all entirely rather than silently taking the first page.
      if (action.hideOnSelectAll && this.isSelectAllActive()) return false;
      if (action.requireAllRowsField) {
        if (!selectedRows.every((row) => row[action.requireAllRowsField!])) return false;
      }
      // Hidden only when the selection contains nothing the action could act on — a mixed
      // selection keeps it, narrowed to the qualifying rows by idsForAction below.
      if (action.restrictSelectedIdsToRowField) {
        if (!selectedRows.some((row) => row[action.restrictSelectedIdsToRowField!])) return false;
      }
      if (action.urlFromRowField) {
        if (!selectedRows[0]?.[action.urlFromRowField]) return false;
      }
      return true;
    });

    if (this.hasRowActionsUrlValue && this.rowActionsUrlValue && count === 1) {
      void this.fetchAndRenderLazyRowActions(selectedRows[0], actionsContainer, selectedIds, filteredStaticActions);
      return;
    }

    const rowActionsField = this.rowActionsFieldValue || "actions";
    const rowActions = selectedRows.map((row) =>
      Array.isArray(row[rowActionsField]) ? (row[rowActionsField] as RowAction[]) : []
    );

    const firstRowActions = rowActions[0] ?? [];
    const commonRowActions =
      rowActions.length === 1
        ? firstRowActions
        : firstRowActions.filter((action) =>
            Boolean(action.appendSelectedIds) &&
            rowActions.slice(1).every((otherActions) =>
              otherActions.some((a) => a.label === action.label)
            )
          );

    this.renderResolvedActions(actionsContainer, commonRowActions, filteredStaticActions, selectedIds, selectedRows, count);
  }

  private renderResolvedActions(
    actionsContainer: Element,
    commonRowActions: RowAction[],
    filteredStaticActions: StaticAction[],
    selectedIds: (string | number)[],
    selectedRows: Array<Record<string, unknown>>,
    count: number
  ): void {
    // The ids an action actually posts: every selected id, unless the action restricts itself to
    // rows carrying a given field (see restrictSelectedIdsToRowField).
    const idsForAction = (action: RowAction | StaticAction): (string | number)[] => {
      const field = action.restrictSelectedIdsToRowField;
      if (!field) return selectedIds;
      return selectedRows
        .filter((row) => row[field])
        .map((row) => row.id)
        .filter((id) => id !== undefined && id !== null) as (string | number)[];
    };

    const resolveSelectedIdsUrl = (action: RowAction | StaticAction, baseUrl: string): string => {
      const actionIds = idsForAction(action);
      if (!action.appendSelectedIds || action.selectedIdsInBody || action.selectionTokenUrl || actionIds.length === 0) return baseUrl;
      const param = action.appendSelectedIdsParam ?? "ids";
      const existing = new Set(new URLSearchParams(baseUrl.split("?")[1] ?? "").getAll(`${param}[]`));
      const idsToAppend = actionIds.filter((id) => !existing.has(String(id)));
      if (idsToAppend.length === 0) return baseUrl;
      const qs = idsToAppend.map((id) => `${param}[]=${id}`).join("&");
      return baseUrl + (baseUrl.includes("?") ? `&${qs}` : `?${qs}`);
    };

    // commonRowActions is always [] for grids with no per-row actions (org table) — the
    // `&&` only ever suppresses real per-row actions (Voter Status) past a single row.
    const showRowActions = count === 1 && commonRowActions.length > 0;

    const title = document.createElement("div");
    title.className = "ag-grid-component-actions-menu__title";

    if (!showRowActions && filteredStaticActions.length === 0) {
      title.textContent = "Actions unavailable for selection";
      actionsContainer.appendChild(title);
      return;
    }

    title.textContent = "Actions";
    actionsContainer.appendChild(title);

    const buildLink = (action: RowAction | StaticAction, url: string) => {
      const link = document.createElement("a");
      link.className = "ag-grid-component-action-link";
      link.href = url;

      if (action.method && action.method !== "get") {
        link.dataset.method = action.method;
        // Opt-out for actions whose endpoint answers with a redirect: the page it lands on IS the
        // refreshed view, so the link has to navigate. An XHR would follow that redirect invisibly
        // and leave the grid showing rows the action just changed.
        if (action.remote !== false) link.dataset.remote = "true";
      }
      // An action whose UI lives outside the grid (a picker that has to be chosen before anything
      // is written) hands the selection over as an event instead of navigating: whoever owns that
      // UI listens for it, and the grid stays the only thing that knows what is selected.
      if (action.emitEvent) {
        const eventName = action.emitEvent;
        link.addEventListener("click", (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.hideActionsMenu();
          document.dispatchEvent(
            new CustomEvent(eventName, { detail: { ids: selectedIds, rows: selectedRows } })
          );
        });
      }
      // Until the token is back the link carries no rel: the modal/iframe openers keyed on `rel`
      // must only see the href that already has the token, then a re-dispatched click reaches them.
      if (action.selectionTokenUrl && action.appendSelectedIds) {
        const tokenUrl = action.selectionTokenUrl;
        const tokenParam = action.selectionTokenParam ?? "selection_token";
        let resolved = false;
        link.addEventListener("click", async (e) => {
          if (resolved) return;
          e.preventDefault();
          e.stopImmediatePropagation();
          const token = await this.storeSelection(tokenUrl, {
            [action.appendSelectedIdsParam ?? "ids"]: idsForAction(action),
          });
          if (!token) return;
          const separator = url.includes("?") ? "&" : "?";
          link.href = `${url}${separator}${tokenParam}=${encodeURIComponent(token)}`;
          if (action.rel) link.rel = action.rel;
          resolved = true;
          link.click();
          requestAnimationFrame(() => {
            resolved = false;
            link.removeAttribute("rel");
            link.href = url;
          });
        });
      } else if (action.rel) {
        link.rel = action.rel;
      }
      if (action.target) {
        link.target = action.target;
      }
      if (action.turbo_frame) {
        const frameId = action.turbo_frame;
        link.addEventListener("click", (e) => {
          e.preventDefault();
          e.stopPropagation();
          const frame = document.querySelector<HTMLElement>(`turbo-frame#${frameId}`);
          if (frame) frame.setAttribute("src", url);
        });
      } else if (action.confirm || action.confirm_title || action.confirm_danger) {
        let bypass = false;
        link.addEventListener("click", async (e) => {
          if (bypass) return;
          e.preventDefault();
          e.stopPropagation();
          // The per-row dropdown menu has always passed all three. This path read `confirm`
          // alone, so an action that gave the dialog a heading got a headless modal here — the
          // same action, two different dialogs depending on which menu opened it.
          const ok = await showConfirmModal({
            title: action.confirm_title,
            danger: action.confirm_danger,
            message: action.confirm,
          });
          if (!ok) return;
          // Download Keys has no XHR to hook a refresh onto — fetch+blob it instead.
          if (action.refreshAfterDownload) {
            const body = action.selectedIdsInBody
              ? { [action.appendSelectedIdsParam ?? "ids"]: idsForAction(action) }
              : undefined;
            await this.downloadAndRefresh(url, body);
            return;
          }
          bypass = true;
          link.click();
          requestAnimationFrame(() => { bypass = false; });
        });
      }

      if (action.icon) {
        const icon = document.createElement("i");
        icon.className = `fa fa-${action.icon}`;
        icon.setAttribute("aria-hidden", "true");
        link.appendChild(icon);
      }

      link.appendChild(document.createTextNode(` ${action.label}`));
      return link;
    };

    if (showRowActions) {
      commonRowActions.forEach((action) => {
        actionsContainer.appendChild(buildLink(action, resolveSelectedIdsUrl(action, action.url || "#")));
      });
      return;
    }

    filteredStaticActions.forEach((action) => {
        if (action.batchDelete) {
          const btn = document.createElement("button");
          btn.className = "ag-grid-component-action-link";
          btn.type = "button";
          if (action.icon) {
            const icon = document.createElement("i");
            icon.className = `fa fa-${action.icon}`;
            icon.setAttribute("aria-hidden", "true");
            btn.appendChild(icon);
          }
          btn.appendChild(document.createTextNode(` ${action.label}`));
          btn.addEventListener("click", async () => {
            const msg = action.confirm ?? `Remove ${selectedIds.length} organization(s)? This cannot be undone.`;
            if (!window.confirm(msg)) return;
            const token = (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement | null)?.content ?? "";
            for (const id of selectedIds) {
              await fetch(`/organizations/${id}`, {
                method: "DELETE",
                headers: { "X-CSRF-Token": token, "X-Requested-With": "XMLHttpRequest" },
              });
            }
            window.location.reload();
          });
          actionsContainer.appendChild(btn);
          return;
        }

        let url = action.url || "#";
        if (action.urlFromRowField) {
          url = String(selectedRows[0]?.[action.urlFromRowField] ?? "#");
        } else if (action.urlTemplate) {
          url = action.urlTemplate.replace("{id}", String(selectedIds[0] ?? ""));
        } else {
          url = resolveSelectedIdsUrl(action, url);
        }
        actionsContainer.appendChild(buildLink(action, url));
      });
  }

  private async fetchAndRenderLazyRowActions(
    row: Record<string, unknown>,
    actionsContainer: Element,
    selectedIds: (string | number)[],
    filteredStaticActions: StaticAction[]
  ): Promise<void> {
    const token = this.lazyRowActionsToken;
    const rowId = rowActionId(row);

    if (rowId === null) {
      this.renderResolvedActions(actionsContainer, [], filteredStaticActions, selectedIds, [row], 1);
      return;
    }

    const cached = this.rowActionsCache.get(String(rowId));
    if (cached) {
      actionsContainer.innerHTML = "";
      this.renderResolvedActions(actionsContainer, cached, filteredStaticActions, selectedIds, [row], 1);
      return;
    }

    const loadingTitle = document.createElement("div");
    loadingTitle.className = "ag-grid-component-actions-menu__title";
    loadingTitle.textContent = "Loading actions…";
    actionsContainer.appendChild(loadingTitle);

    const rowActions = await this.fetchRowActions(rowId);

    if (token !== this.lazyRowActionsToken) return;

    actionsContainer.innerHTML = "";
    this.renderResolvedActions(actionsContainer, rowActions, filteredStaticActions, selectedIds, [row], 1);
  }

  private fetchRowActions(rowId: string | number): Promise<RowAction[]> {
    const key = String(rowId);
    const cached = this.rowActionsCache.get(key);
    if (cached) return Promise.resolve(cached);
    const inflight = this.rowActionsInflight.get(key);
    if (inflight) return inflight;

    const url = this.rowActionsUrlValue.replace("{id}", key);
    const promise = fetch(url, { headers: { Accept: "application/json" }, credentials: "same-origin" })
      .then((response) => (response.ok ? (response.json() as Promise<{ row_actions?: RowAction[] }>) : { row_actions: [] }))
      .then((data) => {
        const actions = Array.isArray(data.row_actions) ? data.row_actions : [];
        this.rowActionsCache.set(key, actions);
        return actions;
      })
      .catch(() => [] as RowAction[])
      .finally(() => this.rowActionsInflight.delete(key));

    this.rowActionsInflight.set(key, promise);
    return promise;
  }

  protected prefetchVisibleRowActions(concurrency = 3): void {
    if (!this.hasRowActionsUrlValue || !this.rowActionsUrlValue || !this.gridApi) return;

    const ids: (string | number)[] = [];
    this.gridApi.forEachNode((node) => {
      const rowId = rowActionId(node.data as Record<string, unknown> | undefined);
      if (rowId === null) return;
      const key = String(rowId);
      if (this.rowActionsCache.has(key) || this.rowActionsInflight.has(key)) return;
      ids.push(rowId);
    });
    if (ids.length === 0) return;

    let cursor = 0;
    const worker = async (): Promise<void> => {
      while (cursor < ids.length) {
        const id = ids[cursor++];
        await this.fetchRowActions(id).catch(() => {});
      }
    };
    for (let i = 0; i < Math.min(concurrency, ids.length); i++) void worker();
  }

  protected resetRowActionsCache(): void {
    this.rowActionsCache.clear();
    this.rowActionsInflight.clear();
  }

  // Collapses the bulk menu without touching the selection — an action that opens its own UI has
  // handed control over, and leaving the menu open behind that UI reads as two competing panels.
  protected hideActionsMenu(): void {
    if (!this.hasActionsMenuTarget) return;

    this.actionsMenuTarget.classList.remove("visible");
  }

  protected clearActions(): void {
    this.lazyRowActionsToken++;
    const actionsContainer = this.actionsMenuTarget.querySelector(
      ".ag-grid-component-actions-menu__actions"
    );
    if (actionsContainer) {
      actionsContainer.innerHTML = "";
    }
  }

  protected buildSideBarConfig(hasAdvanced: boolean): false | SideBarDef {
    if (!hasAdvanced) return false;
    const toolPanels: ToolPanelDef[] = [
      {
        id: "columns",
        labelDefault: "Columns",
        labelKey: "columns",
        iconKey: "columns",
        toolPanel: "agColumnsToolPanel",
        toolPanelParams: {
          suppressColumnSelectAll: false,
          // Opt-in per page (dashboard + vote box): no column there declares
          // enableRowGroup/enablePivot/enableValue, so the drop zones and the pivot
          // toggle would sit permanently empty. Grids that do group/pivot (voter
          // lists, admin audits) leave the flag off and keep the full panel.
          suppressPivotMode: this.hideAggregationPanelsValue,
          suppressRowGroups: this.hideAggregationPanelsValue,
          suppressValues: this.hideAggregationPanelsValue,
          suppressPivots: this.hideAggregationPanelsValue,
        },
      },
      {
        id: "filters-new",
        labelDefault: "Filters",
        labelKey: "filters",
        iconKey: "filter",
        toolPanel: "agNewFiltersToolPanel",
      },
    ];
    return { toolPanels, defaultToolPanel: "" };
  }

  // "Reset Columns" doesn't reset pivotMode by default (it's not part of column state).
  protected withPivotAwareResetColumns(
    params: GetMainMenuItemsParams
  ): (DefaultMenuItem | MenuItemDef)[] {
    return (params.defaultItems ?? []).map((item) => {
      if (item !== "resetColumns") return item;
      return {
        // Matches AG-Grid's own default label (no locale API to look it up dynamically).
        name: "Reset Columns",
        action: () => {
          params.api.resetColumnState();
          if (params.api.isPivotMode()) {
            params.api.setGridOption("pivotMode", false);
          }
          // resetColumnState() reverts widths to their colDef-declared value (usually
          // none, so AG-Grid's generic default) -- re-fit to content, same as initial load.
          this.autoSizeNonFlexColumns();
          this.afterResetColumns();
        },
      } satisfies MenuItemDef;
    });
  }

  // Editable grids (voter-list edit, growable, provision) are too short/narrow for
  // AG-Grid's full column menu to lay out without clipping — keep only Reset Columns.
  protected resetColumnsOnlyMainMenuItems(
    params: GetMainMenuItemsParams
  ): (DefaultMenuItem | MenuItemDef)[] {
    return this.withPivotAwareResetColumns({ ...params, defaultItems: ["resetColumns"] });
  }

  protected basicColDefs(columnDefs: ColDef[]): ColDef[] {
    // enableRowGroup/enablePivot keep a header draggable (ghost + all) even with
    // suppressMovable set — AG-Grid's workOutDraggable() ORs them in regardless.
    return columnDefs.map((c) => ({
      ...c,
      suppressMovable: true,
      enableRowGroup: false,
      enablePivot: false,
    }));
  }

  // Give action cell renderers text to avoid warning (only action buttons, no need for text)
  protected withActionsValueFormatter(columnDefs: ColDef[]): ColDef[] {
    return columnDefs.map((c) =>
      c.cellRenderer === "actionsCellRenderer" && c.valueFormatter === undefined
        ? { ...c, valueFormatter: () => "" }
        : c
    );
  }

  // Columns flagged with context.rankField (e.g. role hierarchy) sort by that numeric
  // row field instead of the displayed string, so ascending/descending follows the
  // domain hierarchy rather than alphabetical order.
  // Descends into ColGroupDef.children for uniformity — inert on the vote-by-vote scored
  // grid today: it's only applied on the initial connect() path, not the question-switch path.
  protected withRankComparator(columnDefs: ColDef[]): ColDef[] {
    return mapColDefTree(columnDefs, (c) => {
      const context = c.context as Record<string, unknown> | undefined;
      const rankField = context?.rankField;
      if (typeof rankField !== "string") return c;

      const valueOrder = Array.isArray(context?.valueOrder) ? (context.valueOrder as string[]) : null;

      return {
        ...c,
        // agSetColumnFilter reuses this comparator to sort its value list, calling it
        // with only (valueA, valueB) — no row nodes — so rank by context.valueOrder then.
        comparator: (valueA: unknown, valueB: unknown, nodeA?: IRowNode, nodeB?: IRowNode) => {
          const rankA = nodeA?.data?.[rankField];
          const rankB = nodeB?.data?.[rankField];
          if (rankA !== undefined && rankB !== undefined) {
            return Number(rankA) - Number(rankB);
          }
          if (valueOrder) {
            return valueOrder.indexOf(String(valueA)) - valueOrder.indexOf(String(valueB));
          }
          return String(valueA ?? "").localeCompare(String(valueB ?? ""));
        },
      };
    });
  }

  // Opt-in: moves the actions menu next to the last-selected row instead of its pinned corner.
  protected setupFollowSelection(): void {
    if (!this.followSelectionValue) return;
    if (this.hasActionsMenuTarget) {
      this.actionsMenuTarget.classList.add("ag-grid-component-actions-menu--follow");
    }
    if (!this.hasGridTarget) return;
    this.gridTarget.addEventListener("mousedown", (e: MouseEvent) => {
      if (!(e.target as HTMLElement).closest(".ag-selection-checkbox")) return;
      this.lastClickX = e.clientX;
    });
    // Grid's internal viewport scrolls independently of the page — track it so the
    // menu can hide/reposition relative to its anchor row.
    this.gridApi?.addEventListener("bodyScroll", () => this.scheduleFollowScrollUpdate());
    this.gridApi?.addEventListener("rowSelected", (e: RowSelectedEvent) => this.handleFollowRowSelected(e));
  }

  private getLoadedSelectedNodes(): IRowNode[] {
    if (!this.gridApi) return [];
    const nodes: IRowNode[] = [];
    const displayedCount = this.gridApi.getDisplayedRowCount();
    for (let i = 0; i < displayedCount; i++) {
      const node = this.gridApi.getDisplayedRowAtIndex(i);
      if (node && !node.group && node.isSelected()) nodes.push(node);
    }
    return nodes;
  }

  // Tracks whichever row was most recently checked, independent of getSelectedNodes()'s
  // row-index ordering; unchecking it falls back to whatever else is still selected.
  private handleFollowRowSelected(event: RowSelectedEvent): void {
    if (!this.followSelectionValue) return;
    if (typeof event.source === "string" && event.source.includes("SelectAll")) {
      const selected = this.getLoadedSelectedNodes();
      this.followAnchorNodeId = selected.length > 0 ? selected[0].id ?? null : null;
      return;
    }
    const node = event.node;
    if (node.isSelected()) {
      this.followAnchorNodeId = node.id ?? null;
    } else if (node.id === this.followAnchorNodeId) {
      const remaining = this.getLoadedSelectedNodes();
      this.followAnchorNodeId = remaining.length > 0 ? remaining[remaining.length - 1].id ?? null : null;
    }
  }

  private getFollowAnchorNode(): IRowNode | null {
    if (!this.gridApi) return null;
    if (this.followAnchorNodeId) {
      const node = this.gridApi.getRowNode(this.followAnchorNodeId);
      if (node?.isSelected()) return node;
    }
    const selectedNodes = this.getLoadedSelectedNodes();
    return selectedNodes.length > 0 ? selectedNodes[selectedNodes.length - 1] : null;
  }

  private scheduleFollowScrollUpdate(): void {
    if (this.pendingFollowScrollFrame) return;
    this.pendingFollowScrollFrame = true;
    requestAnimationFrame(() => {
      this.pendingFollowScrollFrame = false;
      this.updateFollowScrollVisibility();
    });
  }

  protected updateFollowScrollVisibility(): void {
    if (!this.followSelectionValue || !this.gridApi || !this.hasActionsMenuTarget) return;

    // getFollowAnchorNode() trips warning #199 during selectAll — use the SSRM-provided
    // anchor instead so the menu still follows scroll.
    const anchorNode = this.isSelectAllActive() ? this.getSelectAllAnchorNode() : this.getFollowAnchorNode();
    if (!anchorNode) return; // handleSelectionChanged already hides the menu when nothing's selected

    const rowEl = this.gridTarget.querySelector(
      `[row-id="${CSS.escape(String(anchorNode.id))}"]`
    ) as HTMLElement | null;
    // Row-index range APIs include AG-Grid's offscreen row buffer, so compare actual
    // geometry against the scrollable viewport instead.
    const viewportEl = this.gridTarget.querySelector(".ag-body-viewport") as HTMLElement | null;

    if (!rowEl || !viewportEl) {
      this.actionsMenuTarget.style.display = "none";
      return;
    }

    const rowRect = rowEl.getBoundingClientRect();
    const viewportRect = viewportEl.getBoundingClientRect();
    const isVisible = rowRect.bottom > viewportRect.top && rowRect.top < viewportRect.bottom;

    this.actionsMenuTarget.style.display = isVisible ? "" : "none";
    // Anchor to the row's own position, not the last mousedown — the two can drift
    // apart during fast scrolling.
    if (isVisible && !this.menuDragged) {
      const columnX = this.resolveFollowSelectionColumnX(rowEl);
      this.positionMenuNearClick(rowRect.top + rowRect.height / 2, columnX ?? this.lastClickX ?? 0);
    }
  }

  private resolveFollowSelectionColumnX(rowEl: HTMLElement): number | null {
    if (!this.hasFollowSelectionColumnValue || !this.followSelectionColumnValue) return null;
    const cellEl = rowEl.querySelector(
      `[col-id="${CSS.escape(this.followSelectionColumnValue)}"]`
    ) as HTMLElement | null;
    if (!cellEl) return null;
    return cellEl.getBoundingClientRect().left;
  }

  // Overridden by SSRM (getServerSideSelectionState().selectAll can be true there, with no
  // single row to track); always false for CSRM, which has no such concept.
  protected isSelectAllActive(): boolean {
    return false;
  }

  // Overridden by SSRM to anchor on the last loaded row during a true select-all. Base default
  // is unreachable in practice since isSelectAllActive() is always false here.
  protected getSelectAllAnchorNode(): IRowNode | null {
    return null;
  }

  protected positionMenuNearClick(clientY: number, clientX: number): void {
    const menu = this.actionsMenuTarget;
    const wrapper = menu.parentElement;
    if (!wrapper) return;

    const menuWidth = menu.offsetWidth || 275;
    const menuHeight = menu.offsetHeight || 220;

    const wrapperRect = wrapper.getBoundingClientRect();
    const selectionColWidth = this.gridApi?.getAllDisplayedColumns()[0]?.getActualWidth() ?? 48;

    // Clamp against both viewport bottom and the grid's own bottom edge, so the menu
    // can't overflow past where the grid ends even if the viewport has room below it.
    const viewportMaxTop = window.innerHeight - menuHeight - 16;
    const gridRect = this.hasGridTarget ? this.gridTarget.getBoundingClientRect() : null;
    const gridMaxTop = gridRect ? gridRect.bottom - menuHeight - 8 : viewportMaxTop;
    const maxTop = Math.min(viewportMaxTop, gridMaxTop);
    const top = Math.min(Math.max(clientY + 15, 0), Math.max(maxTop, 0));

    const minLeft = wrapperRect.left + selectionColWidth + 8;
    const maxLeft = window.innerWidth - menuWidth - 8;
    const left = Math.min(Math.max(clientX + 8, minLeft), Math.max(maxLeft, minLeft));

    // Absolute (not fixed) positioning is relative to the wrapper's box, so the menu
    // scrolls away with the page instead of staying glued to the screen.
    menu.style.top = `${top - wrapperRect.top}px`;
    menu.style.left = `${left - wrapperRect.left}px`;
    menu.style.right = "auto";
    menu.style.bottom = "auto";
  }

  // Drag the actions menu by its "N selected" header. Dragging pins it: the
  // follow-selection scroll handler stops repositioning it until the selection clears.
  protected setupMenuDrag(): void {
    if (!this.hasActionsMenuTarget || !this.draggableMenuValue) return;
    const menu = this.actionsMenuTarget;
    // Marks the grip/cursor affordance — CSS scopes both to this class, so an opted-out
    // grid shows no hint that the menu can be moved.
    menu.classList.add("ag-grid-component-actions-menu--draggable");
    if (this.hasSelectedCountTarget) this.selectedCountTarget.title = "Drag to move";

    menu.addEventListener("mousedown", (down: MouseEvent) => {
      if (down.button !== 0) return;
      const target = down.target as HTMLElement;
      // Only the header grabs — anywhere else a mousedown belongs to an action link.
      if (!target.closest(".ag-grid-component-actions-menu__count")) return;

      const wrapperRect = menu.parentElement!.getBoundingClientRect();
      const startRect = menu.getBoundingClientRect();
      const grabX = down.clientX - startRect.left;
      const grabY = down.clientY - startRect.top;
      let moved = false;

      const onMove = (move: MouseEvent) => {
        if (!moved && Math.hypot(move.clientX - down.clientX, move.clientY - down.clientY) < 4) return;
        moved = true;
        this.menuDragged = true;
        menu.classList.add("ag-grid-component-actions-menu--dragging");

        const left = Math.min(
          Math.max(move.clientX - grabX, 8),
          window.innerWidth - startRect.width - 8
        );
        const top = Math.min(
          Math.max(move.clientY - grabY, 8),
          window.innerHeight - startRect.height - 8
        );
        menu.style.left = `${left - wrapperRect.left}px`;
        menu.style.top = `${top - wrapperRect.top}px`;
        menu.style.right = "auto";
        menu.style.bottom = "auto";
      };

      const onUp = () => {
        document.removeEventListener("mousemove", onMove);
        document.removeEventListener("mouseup", onUp);
        menu.classList.remove("ag-grid-component-actions-menu--dragging");
      };

      document.addEventListener("mousemove", onMove);
      document.addEventListener("mouseup", onUp);
      down.preventDefault(); // stop the drag from selecting the count text
    });
  }

  // Clears inline positioning, restoring the CSS-driven default (pinned corner).
  protected resetMenuPosition(): void {
    if (!this.hasActionsMenuTarget) return;
    this.menuDragged = false;
    const menu = this.actionsMenuTarget;
    menu.style.top = "";
    menu.style.left = "";
    menu.style.right = "";
    menu.style.bottom = "";
  }
}
